import * as React from "react";
import {useEffect, useState} from "react";
import {useQuery } from "@tanstack/react-query";
import CasesRepository from "../../repositories/CasesRepository";
import MigrationStatusChip from "../common/MigrationStatusChip";
import SortableTableColumnHeader from "../common/SortableTableColumnHeader";
import {useTableHandlers} from "../common/Table/Hooks/UseTableHandlers";
import {useTableFileFilters} from "../common/Table/Hooks/UseTableFileFilters";
import {QUERY_KEYS} from "../../constants/QueryKeys";
import {QUERY_CONFIG} from "../../constants/QueryConfig";
import {fileFiltersBoxClassName, tableWrapperClassName} from "../common/Table/TableStyles";
import {SearchInput} from "../common/Table/SearchInput";
import {FileStatusFilter} from "../common/Table/File/FileStatusFilter";
import TablePagination from "../common/Table/TablePagination";
import { EmptyFolder } from "../../models/EmptyFolder";
import { cn } from "@/lib/utils";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";

interface CaseEmptyFoldersTableProps {
  caseId: string;
  toolbar?: React.ReactNode;
}

export const CaseEmptyFoldersTable: React.FC<CaseEmptyFoldersTableProps> = ({
 caseId,
 toolbar,
}: CaseEmptyFoldersTableProps) => {
  const casesRepository = new CasesRepository();
  const [caseEmptyFolders, setCaseEmptyFolders] = useState<EmptyFolder[]>([]);

  const {
    order,
    sort,
    page,
    rowsPerPage,
    rows,
    setRows,
    setPage,
    changeSort,
    changeOrder,
    handleRowsPerPageChange,
  } = useTableHandlers({
    order: "asc",
    sort: "status",
    page: 0,
    rowsPerPage: 100,
  });

  const {
    statusFilter,
    destinationPathFilter,
    setStatusFilter,
    handleDestinationPathSearch,
  } = useTableFileFilters();

  const caseEmptyFoldersQuery = useQuery({ queryKey: [
      QUERY_KEYS.CASE_EMPTY_FOLDERS,
      caseId,
      page,
      rowsPerPage,
      statusFilter,
      sort,
      order,
      destinationPathFilter,
    ], queryFn:
    async () => await casesRepository.getCaseEmptyFoldersForCase(caseId, {
      status: statusFilter ? [statusFilter] : null,
      uploadPathFromRoot: destinationPathFilter,
      descending: order === "desc",
      orderBy: sort,
      pageNumber: page + 1,
      pageSize: rowsPerPage,
    }), staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
      refetchInterval: QUERY_CONFIG.DEFAULT_REFETCH_INTERVAL,
      refetchIntervalInBackground: true,
      refetchOnMount: true, });

  useEffect(() => {
    if (caseEmptyFoldersQuery.data?.data) {
      setCaseEmptyFolders(caseEmptyFoldersQuery.data?.data);
      setRows(caseEmptyFoldersQuery.data.paging?.totalCount ?? 0);
    }
  }, [caseEmptyFoldersQuery]);

  return (
    <div className="flex flex-col w-full flex-1 min-h-0">
      {toolbar}
      <div className={fileFiltersBoxClassName}>
        <SearchInput
          label={"Search empty folders"}
          placeholder={"Folder path"}
          onBlur={(e) => handleDestinationPathSearch(e.target.value)}
        />
        <FileStatusFilter
          value={statusFilter}
          onChange={(newValue) => {
            setPage(0);
            setStatusFilter(newValue);
          }}
        />
      </div>
      <div
        className={cn(
          tableWrapperClassName,
          (caseEmptyFoldersQuery.isFetching || caseEmptyFoldersQuery.isError) && "opacity-50 pointer-events-none"
        )}
      >
        <Table containerClassName="flex-1 min-h-0">
            <TableHeader>
              <TableRow>
                <TableHead className="w-2.5" />
                <TableHead className="w-[35%]">
                  Folder ID
                </TableHead>
                <TableHead className="w-[15%]">
                  <SortableTableColumnHeader
                    label={"Status"}
                    sortProperty={"status"}
                    getSort={() => sort}
                    setSort={(newSort) => changeSort(newSort)}
                    getOrder={() => order}
                    setOrder={(newOrder) => changeOrder(newOrder)}
                  />
                </TableHead>
                <TableHead className="w-[50%]">
                  Sharepoint folder path
                </TableHead>
                <TableHead className="w-2.5" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {caseEmptyFolders.map((caseEmptyFolder) => (
                <TableRow key={caseEmptyFolder.id}>
                  <TableCell />
                  <TableCell>
                    <span className="text-sm">{caseEmptyFolder.id}</span>
                  </TableCell>
                  <TableCell>
                    <MigrationStatusChip status={caseEmptyFolder.status} />
                  </TableCell>
                  <TableCell className="whitespace-normal break-all">
                    {caseEmptyFolder.uploadPathFromRoot}
                  </TableCell>
                  <TableCell />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        <TablePagination
          page={page}
          rowsPerPage={rowsPerPage}
          rows={rows}
          onPageChange={setPage}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </div>
    </div>
  );
};
